import {
	FileDoneOutlined,
	FileSearchOutlined,
	PlusOutlined,
	ReloadOutlined,
	WarningOutlined,
} from "@ant-design/icons";
import { Button } from "antd";
import { ReactNode } from "react";
import { ReportStreamView } from "../../../hooks/useReportStream";
import { ReportItem } from "../../../hooks/useReportStreamFilter";
import { capitalizeFirstLetter } from "../../../utils/string_convert_helper";
import {
	ReportStreamFigures,
	ReportStreamToolbar,
} from "./ReportStreamFilters";
import ReportStreamList, { ReportStreamColumn } from "./ReportStreamList";

type StreamMessageProps = {
	icon: ReactNode;
	title: string;
	text: string;
	action: ReactNode;
	// A failure, announced as it shows.
	isAlert?: boolean;
};

// What the list shows when it has no rows to show.
function StreamMessage({
	icon,
	title,
	text,
	action,
	isAlert = false,
}: Readonly<StreamMessageProps>) {
	return (
		<div
			className="report-stream__message"
			role={isAlert ? "alert" : undefined}
		>
			{icon}
			<p className="report-stream__message-title">{title}</p>
			<p className="report-stream__message-text">{text}</p>
			{action}
		</div>
	);
}

type ReportStreamPageProps<T extends ReportItem> = {
	title: string;
	subtitle: string;
	// "audit" and "audits": how the page's own words name the reports.
	noun: string;
	plural: string;
	newLabel: string;
	onNew: () => void;
	// Under "No audits yet": which reports will show here.
	emptyText: string;
	searchPlaceholder: string;
	view: ReportStreamView<T>;
	// Until the reports first load, when that failed, and while trying again.
	isLoading: boolean;
	loadFailed: boolean;
	isRetrying: boolean;
	onRetry: () => void;
	columns: readonly ReportStreamColumn<T>[];
	getRowKey: (row: T) => string;
	renderRowMenu: (row: T) => ReactNode;
};

// A report stream's page: a heading with the button that adds a report, the
// status figures, the search and filters, how many reports they leave, and
// the list, or why there's nothing to list.
function ReportStreamPage<T extends ReportItem>({
	title,
	subtitle,
	noun,
	plural,
	newLabel,
	onNew,
	emptyText,
	searchPlaceholder,
	view,
	isLoading,
	loadFailed,
	isRetrying,
	onRetry,
	columns,
	getRowKey,
	renderRowMenu,
}: Readonly<ReportStreamPageProps<T>>) {
	const hasData = !isLoading && !loadFailed;

	let message: ReactNode;
	if (loadFailed) {
		message = (
			<StreamMessage
				icon={
					<WarningOutlined
						className="report-stream__message-icon report-stream__message-icon--warning"
						aria-hidden="true"
					/>
				}
				title={`${capitalizeFirstLetter(plural)} couldn't be loaded`}
				text="Something went wrong while fetching them. Try again, and if it keeps happening, contact support."
				action={
					<Button
						icon={<ReloadOutlined aria-hidden="true" />}
						loading={isRetrying}
						onClick={onRetry}
					>
						Try again
					</Button>
				}
				isAlert
			/>
		);
	} else if (hasData && view.total === 0) {
		message = (
			<StreamMessage
				icon={
					<FileDoneOutlined
						className="report-stream__message-icon"
						aria-hidden="true"
					/>
				}
				title={`No ${plural} yet`}
				text={emptyText}
				action={
					<Button
						type="primary"
						icon={<PlusOutlined aria-hidden="true" />}
						onClick={onNew}
					>
						{newLabel}
					</Button>
				}
			/>
		);
	} else if (hasData && view.shownCount === 0) {
		// With the filters as they start, only a status switched off by
		// default, such as Closed, can be hiding the reports.
		message = (
			<StreamMessage
				icon={
					<FileSearchOutlined
						className="report-stream__message-icon"
						aria-hidden="true"
					/>
				}
				title={`No ${plural} match these filters`}
				text={
					view.filtersActive
						? "Try another search or status, or reset the filters."
						: `The ${plural} here are at statuses that are switched off.`
				}
				action={
					view.filtersActive ? (
						<Button onClick={view.reset}>Reset filters</Button>
					) : (
						<Button onClick={() => view.setStatuses([...view.figures])}>
							Show all statuses
						</Button>
					)
				}
			/>
		);
	}

	return (
		<div className="report-stream">
			<div className="report-stream__head">
				<div className="report-stream__heading">
					<h1 className="report-stream__title">{title}</h1>
					<p className="report-stream__subtitle">{subtitle}</p>
				</div>
				<Button
					type="primary"
					className="report-stream__new"
					icon={<PlusOutlined aria-hidden="true" />}
					onClick={onNew}
				>
					{newLabel}
				</Button>
			</div>

			<ReportStreamFigures
				label={`Show ${plural} by status`}
				figures={view.figures}
				selected={view.statuses}
				counts={hasData ? view.counts : undefined}
				onChange={view.setStatuses}
			/>

			<ReportStreamToolbar
				searchLabel={`Search ${plural}`}
				searchPlaceholder={searchPlaceholder}
				searchValue={view.searchInput}
				onSearchChange={view.setSearchInput}
				selects={view.selects}
			/>

			{hasData && view.total > 0 && (
				<p className="report-stream__results">
					<span aria-live="polite">
						{`${view.shownCount} of ${view.total} ${view.total === 1 ? noun : plural}`}
					</span>
					{view.filtersActive && (
						<button
							type="button"
							className="report-stream__text-button"
							onClick={view.reset}
						>
							Reset filters
						</button>
					)}
				</p>
			)}

			<ReportStreamList
				label={capitalizeFirstLetter(plural)}
				columns={columns}
				rows={view.page.rows}
				getRowKey={getRowKey}
				renderRowMenu={renderRowMenu}
				sort={view.sort}
				onSortChange={view.setSort}
				isLoading={isLoading}
				loadingText={`Loading ${plural}`}
				message={message}
				pagination={{
					page: view.page.page,
					pageSize: view.page.pageSize,
					from: view.page.from,
					to: view.page.to,
					total: view.shownCount,
					onPageChange: view.setPage,
					onPageSizeChange: view.setPageSize,
				}}
			/>
		</div>
	);
}

export default ReportStreamPage;
